import { useTranslations } from '@/shared/i18n';
import type { BotLogin } from '@/shared/use-bot-login';

/**
 * Вход через бота: кнопка и всё, что происходит, пока человек в Telegram.
 *
 * Общий на лендинг и диалог. Разница между ними — только в поводе войти, а
 * сам путь один: уйти к боту, нажать «Старт», вернуться на вкладку.
 */

/** Логотип Telegram: встроенный, чтобы не тянуть внешний файл. */
export function TelegramIcon(): React.ReactNode {
  return (
    <svg viewBox="0 0 24 24" className="size-5 shrink-0" fill="currentColor" aria-hidden>
      <path d="M21.94 4.6l-3.02 14.25c-.23 1.01-.83 1.26-1.68.78l-4.64-3.42-2.24 2.16c-.25.25-.46.46-.94.46l.33-4.73 8.6-7.77c.37-.33-.08-.52-.58-.19l-10.63 6.69-4.58-1.43c-1-.31-1.02-1 .21-1.48L20.64 3.1c.83-.3 1.56.2 1.3 1.5z" />
    </svg>
  );
}

export function TelegramLoginButton({
  botLogin,
  label,
}: {
  botLogin: BotLogin;
  /** Своя надпись, если повод войти требует других слов. */
  label?: string;
}): React.ReactNode {
  const dictionary = useTranslations();

  return (
    <>
      {/*
        Оформление вторичное: сплошная заливка фирменным синим слишком похожа
        на основную кнопку, и главное действие переставало выделяться.
      */}
      <button
        type="button"
        disabled={botLogin.state === 'starting'}
        onClick={botLogin.begin}
        className="flex min-h-[52px] w-full items-center justify-center gap-2.5 rounded-2xl
                   border border-[#229ED9]/40 bg-[#229ED9]/10 px-5 text-[16px] font-medium text-[#4FB6E8]
                   transition-[transform,background-color] duration-150
                   hover:bg-[#229ED9]/20 active:scale-[0.985] disabled:opacity-60"
      >
        <TelegramIcon />
        {label ?? dictionary.auth.action}
      </button>

      {botLogin.state === 'waiting' ? (
        <div className="flex flex-col items-center gap-1">
          <p className="text-[13px] text-text-muted">{dictionary.auth.waiting}</p>
          {/*
            Запасной путь: вкладку мог не пустить браузер, и тогда открыть
            Telegram человеку нужно самому — обычной ссылкой.
          */}
          {botLogin.url ? (
            <a
              href={botLogin.url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[13px] font-medium text-[#4FB6E8] underline-offset-2 hover:underline"
            >
              {dictionary.auth.openAgain}
            </a>
          ) : null}
          <button
            type="button"
            onClick={botLogin.cancel}
            className="text-[12px] text-text-muted underline-offset-2 hover:underline"
          >
            {dictionary.auth.cancel}
          </button>
        </div>
      ) : null}

      {botLogin.state === 'expired' || botLogin.state === 'failed' ? (
        <p className="text-[13px] text-danger">
          {botLogin.state === 'expired' ? dictionary.auth.expired : dictionary.auth.failed}
        </p>
      ) : null}
    </>
  );
}
